import { Controller } from '@hotwired/stimulus'

export default class InvestRobotController extends Controller {
  static targets = [
    'advancedSettings',
    'advancedSettingsToggler',
    'warranty',
    'projectTypology',
    'defaultWarrantyPredicate',
    'minMortgagePercentageSection',
    'minTrustCoveragePercentageSection',
    'maxAmountTotal',
    'maxTotalWarning',
    'warrantyCombinationPredicateSection',
    'projectKindRadio',
    'operationCriteria',
    'rentalCriteria',
    'rentalTypology',
  ];

  static values = {
    maxTotal: Number,
  }

  connect() {
    this.scrollHandler = this.scrollToFirstErrorOrTop.bind(this);
    this.element.addEventListener('turbo:frame-load', this.scrollHandler);
  }

  disconnect() {
    this.element.removeEventListener('turbo:frame-load', this.scrollHandler);
  }

  rentalCriteriaTargetConnected() {
    this.toggleProjectKind();
    this.checkMaxAmountTotal();
  }

  toggleProjectKind() {
    const rentalSelected = this.selectedProjectKind() === 'rental';

    if (this.hasOperationCriteriaTarget) {
      this.setCriteriaBlockEnabled(this.operationCriteriaTarget, !rentalSelected);
    }

    if (this.hasRentalCriteriaTarget) {
      this.setCriteriaBlockEnabled(this.rentalCriteriaTarget, rentalSelected);
      if (rentalSelected) { this.ensureRentalSectorsSelected(); }
    }
  }

  selectedProjectKind() {
    const checkedRadio = this.projectKindRadioTargets.find((radio) => radio.checked);

    return checkedRadio ? checkedRadio.value : 'operation';
  }

  setCriteriaBlockEnabled(block, enabled) {
    block.classList.toggle('d-none', !enabled);
    block.querySelectorAll('input, select, textarea').forEach((field) => {
      field.disabled = !enabled;
    });
  }

  ensureRentalSectorsSelected() {
    const anySelected = this.rentalTypologyTargets.some((checkbox) => checkbox.checked);
    if (anySelected) { return; }

    this.rentalTypologyTargets.forEach((checkbox) => { checkbox.checked = true; });
  }

  advancedSettingsTogglerTargetConnected() {
    this.toggleAdvancedSettings();
    this.toggleMinMortagePercentageSection();
    this.toggleMinTrustCoveragePercentageSection();
    this.toggleWarrantyCombinationPredicateSection();
    this.checkMaxAmountTotal();
  }

  scrollToFirstErrorOrTop() {
    const firstError = this.element.querySelector('.is-invalid, .invalid-feedback');

    if (firstError) {
      firstError.scrollIntoView({behavior: 'smooth', block: 'center'});
    } else {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }

  toggleAdvancedSettings() {
    if (this.advancedSettingsTogglerTarget.checked) {
      new bootstrap.Collapse(this.advancedSettingsTarget, {toggle: false}).show();
    } else {
      new bootstrap.Collapse(this.advancedSettingsTarget, {toggle: false}).hide();
    }
  }

  clearAdvancedSettings() {
    this.advancedSettingsTarget.querySelectorAll('input,select').forEach(input => {
      if (input.type === 'checkbox') {
        input.checked = false;
      } else if (input.type === 'radio') {
        input.checked = false;
      } else if (input.type === 'text') {
        input.value = '';
      } else if (input.type === 'number') {
        input.value = 0;
      }
    });

    this.defaultWarrantyPredicateTarget.checked = true;
    this.projectTypologyTargets.forEach((typology) => {
      typology.checked = true;
    });
    this.toggleMinMortagePercentageSection();
    this.toggleMinTrustCoveragePercentageSection();
    this.toggleWarrantyCombinationPredicateSection();
  }

  toggleAndClearAdvancedSettings() {
    this.toggleAdvancedSettings();
    this.clearAdvancedSettings();
  }

  // the user is only allowed to select two warranties when advanced settings are checked
  checkWarrantyLimit(event) {
    if (!this.advancedSettingsTogglerTarget.checked) { return true; }

    if (this.warrantyTargets.filter((w) => w.checked).length > 2) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    this.toggleMinMortagePercentageSection();
    this.toggleMinTrustCoveragePercentageSection();
    this.toggleWarrantyCombinationPredicateSection();

    return true;
  }

  toggleWarrantyCombinationPredicateSection() {
    if (this.warrantyTargets.filter((w) => w.checked).length > 1) {
      this.warrantyCombinationPredicateSectionTarget.classList.remove('d-none');
    } else {
      this.warrantyCombinationPredicateSectionTarget.classList.add('d-none');
    }
  }

  toggleMinMortagePercentageSection() {
    const hasMortgageWarranty = !!this.warrantyTargets.find(
      (w) => {
        return ['first_rank_mortgage', 'second_rank_mortgage'].includes(w.value) && w.checked;
      }
    );

    if (hasMortgageWarranty) {
      this.minMortgagePercentageSectionTarget.classList.remove('d-none');
    } else {
      this.minMortgagePercentageSectionTarget.classList.add('d-none');
    }
  }

  toggleMinTrustCoveragePercentageSection() {
    if (!this.hasMinTrustCoveragePercentageSectionTarget) { return; }

    const hasTrustWarranty = !!this.warrantyTargets.find(
      (w) => w.value === 'trust' && w.checked
    );

    if (hasTrustWarranty) {
      this.minTrustCoveragePercentageSectionTarget.classList.remove('d-none');
    } else {
      this.minTrustCoveragePercentageSectionTarget.classList.add('d-none');
    }
  }

  checkMaxAmountTotal() {
    if (this.maxAmountTotalTarget.value > this.maxTotalValue) {
      this.maxAmountTotalTarget.classList.add('is-warning');
      this.maxTotalWarningTarget.classList.remove('d-none');
    } else {
      this.maxAmountTotalTarget.classList.remove('is-warning');
      this.maxTotalWarningTarget.classList.add('d-none');
    }
  }
}
